import { useState } from 'react';
import { UNIFIED_DATA_VERSION } from '@hakimo-ui/hakimo/types';
import { useApiDataVersion } from './useApiDataVersion';

/**
 * Runs `reset` when the data version flips, and returns the current one.
 *
 * The reset happens DURING RENDER rather than in an effect, which is the
 * whole reason this is a hook and not a `useEffect` at each call site: child
 * effects run before a parent's, so a child would otherwise fetch with the
 * previous dataset's id (v2-numeric vs v3-uuid) before the parent could clear
 * it. React supports this as the "adjust state when a prop changes" pattern —
 * the component re-renders immediately and the second pass is stable.
 *
 * `reset` must only set state on the calling component. State held in an
 * external store (a jotai atom, say) cannot be written mid-render without
 * re-rendering every subscriber, so reset that in an effect keyed on the
 * returned version.
 */
export function useOnDataVersionChange(
  reset: () => void
): typeof UNIFIED_DATA_VERSION | undefined {
  const dataVersion = useApiDataVersion();
  const [renderedVersion, setRenderedVersion] = useState(dataVersion);

  if (renderedVersion !== dataVersion) {
    setRenderedVersion(dataVersion);
    reset();
  }

  return dataVersion;
}
